// The screen a closing on-screen keyboard is about to leave. Start game with
// a name field focused opens the score-limit modal as the keyboard slides
// away: on Brave for iOS the visible height grew 356 → 674 in ~15 steps, and
// the modal, centred on each, faded in sliding 150px down while the page
// under it was pulled down step by step (trace from the user's iPhone,
// 2026-10-28). A modal opening then centres on, and holds the page for, the
// screen it's about to have.

/** A keyboard that stops short of giving the whole screen back (a toolbar
 *  came back too) stops counting this long after the screen last changed;
 *  iOS moves it every frame and closes it in ~400ms. */
const SETTLE_MS = 1000;

let before: number | null = null;
let width = 0;
let timer: number | undefined;

const visibleHeight = () => window.visualViewport?.height ?? window.innerHeight;

const isTextField = (node: unknown): node is HTMLElement =>
  (node instanceof HTMLInputElement &&
    !/^(button|checkbox|color|file|hidden|image|radio|range|reset|submit)$/.test(
      node.type,
    )) ||
  node instanceof HTMLTextAreaElement ||
  (node instanceof HTMLElement && node.isContentEditable);

const forget = () => {
  before = null;
  window.clearTimeout(timer);
};
const forgetLater = () => {
  window.clearTimeout(timer);
  timer = window.setTimeout(forget, SETTLE_MS);
};

/**
 * The visible height from before a page field's keyboard opened, while that
 * keyboard is closing (no field has focus and the screen is still shorter),
 * else null. A modal's own field is left alone: its keyboard belongs to it.
 */
export function closingKeyboardHeight(): number | null {
  if (before === null || isTextField(document.activeElement)) return null;
  return visibleHeight() < before - 1 ? before : null;
}

/** Follows page fields and the keyboard; returns a function that stops. */
export function trackClosingKeyboard(): () => void {
  const onFocusIn = (event: FocusEvent) => {
    if (!isTextField(event.target)) return;
    window.clearTimeout(timer);
    if (before === null && !event.target.closest('[role="dialog"]')) {
      before = visibleHeight();
      // innerWidth, not the visual width: a desktop scrollbar changes that.
      width = window.innerWidth;
    }
  };
  const onFocusOut = (event: FocusEvent) => {
    if (before !== null && !isTextField(event.relatedTarget)) forgetLater();
  };
  const onResize = () => {
    if (before === null) return;
    if (window.innerWidth !== width) return forget();
    if (isTextField(document.activeElement)) return;
    if (visibleHeight() >= before - 1) forget();
    else forgetLater();
  };
  const viewport = window.visualViewport;
  document.addEventListener('focusin', onFocusIn, true);
  document.addEventListener('focusout', onFocusOut, true);
  viewport?.addEventListener('resize', onResize);
  window.addEventListener('resize', onResize);
  return () => {
    forget();
    document.removeEventListener('focusin', onFocusIn, true);
    document.removeEventListener('focusout', onFocusOut, true);
    viewport?.removeEventListener('resize', onResize);
    window.removeEventListener('resize', onResize);
  };
}
